import { useEffect, useRef, useState } from "react";
import { Box, Checkbox } from "@mui/material";

interface SyncedVideoProps {
  src: string;
  isPlaying: boolean;
  currentTime: number;
  multiVid?: boolean;
  onLoaded: (duration: number) => void;
  onFrameClick?: (videoUrl: string, time: number) => void;
  onPlayPauseChange?: (playing: boolean) => void;
  onTimeUpdate?: () => void;
  onEnded?: () => void;
  storeRef?: (ref: HTMLVideoElement | null) => void;
}

const SyncedVideo: React.FC<SyncedVideoProps> = ({
  src,
  isPlaying,
  currentTime,
  multiVid = true,
  onLoaded,
  onFrameClick,
  onPlayPauseChange,
  onTimeUpdate,
  onEnded,
  storeRef,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isActive, setIsActive] = useState(true);

  // Share videoRef with parent
  useEffect(() => {
    storeRef?.(videoRef.current);
  }, [videoRef.current]);

  // Sync current time when updated by parent
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !isActive) return;
    if (Math.abs(v.currentTime - currentTime) > 0.03) {
      v.currentTime = currentTime;
    }
  }, [currentTime, isActive]);

  // Sync play/pause when parent state changes
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !isActive) return;

    if (isPlaying) {
      const playPromise = v.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {}); // Autoplay restrictions, ignore
      }
    } else {
      v.pause();
    }
  }, [isPlaying, isActive]);

  // Handle local checkbox toggle
  const toggleActive = () => {
    const v = videoRef.current;
    const newState = !isActive;
    setIsActive(newState);

    if (!v) return;

    if (newState) {
      // ✅ Became active — seek and optionally play
      v.currentTime = currentTime;
      if (isPlaying) v.play();
    } else {
      // 🚫 Became inactive — pause and reset
      v.pause();
      v.currentTime = 0;
    }
  };

  if (!multiVid) return (
    <video
        ref={videoRef}
        src={src}
        crossOrigin="anonymous"
        preload="true"
        playsInline
        onLoadedMetadata={() => {
          if (videoRef.current) onLoaded(videoRef.current.duration);
        }}
        onClick={() => {
          if (onFrameClick && videoRef.current) {
            onFrameClick(src, videoRef.current.currentTime);
          }
        }}
        onTimeUpdate={onTimeUpdate}
        onPlay={() => onPlayPauseChange?.(true)}
        onPause={() => onPlayPauseChange?.(false)}
        onEnded={() => {
          const v = videoRef.current;
          if (v) {
            v.pause();
            v.currentTime = 0;
          }
          onEnded?.();
        }}
        controls={false}
        style={{
          width: "100%",
          height: "auto",
          maxHeight: "65vh",
          objectFit: "contain",
          borderRadius: 8,
          backgroundColor: "#000",
          opacity: isActive ? 1 : 0.5,
          transition: "opacity 0.3s",
        }}
      />
  )

  return (
    <Box
      sx={{
        position: "relative",
        width: "50%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Checkbox
        checked={isActive}
        onChange={toggleActive}
        sx={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 3,
          color: "#fff",
          borderRadius: "6px",
          padding: "4px",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          boxShadow: "0 0 6px rgba(0,0,0,0.5)",
          transition: "all 0.2s ease",
          '&:hover': {
            backgroundColor: isActive
              ? "rgba(25, 118, 210, 1)"
              : "rgba(255, 255, 255, 0.3)",
            transform: "scale(1.1)",
          },
          '& .MuiSvgIcon-root': {
            fontSize: 28,
            color: isActive ? "#fff" : "#ccc",
          },
        }}
      />

      <video
        ref={videoRef}
        src={src}
        crossOrigin="anonymous"
        preload="true"
        playsInline
        onLoadedMetadata={() => {
          if (videoRef.current) onLoaded(videoRef.current.duration);
        }}
        onClick={() => {
          if (onFrameClick && videoRef.current) {
            onFrameClick(src, videoRef.current.currentTime);
          }
        }}
        onTimeUpdate={onTimeUpdate}
        onPlay={() => onPlayPauseChange?.(true)}
        onPause={() => onPlayPauseChange?.(false)}
        onEnded={() => {
          const v = videoRef.current;
          if (v) {
            v.pause();
            v.currentTime = 0;
          }
          onEnded?.();
        }}
        controls={false}
        style={{
          width: "100%",
          height: "auto",
          maxHeight: "65vh",
          objectFit: "contain",
          borderRadius: 8,
          backgroundColor: "#000",
          opacity: isActive ? 1 : 0.5,
          transition: "opacity 0.3s",
        }}
      />
    </Box>
  );
};

export default SyncedVideo;
